import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle, Brain, Calendar, Car, Clock, DollarSign, Eye, FileText,
  Receipt, RefreshCw, Search, Settings, ShieldCheck, Stethoscope, User, X
} from 'lucide-react';
import { DataTable } from '../../common/DataTable';
import Card from '../../common/Card';
import Badge from '../../common/Badge';
import EmptyState from '../../common/EmptyState';
import Button from '../../common/Button';
import { Modal } from '../../common/Modal';
import FormField, { Input, Textarea } from '../../common/FormField';
import { jobAPI } from '../../../services/api';
import { lineTotal } from '../../../lib/quantity';
import { STATUS_TONE, PRIORITY_TONE } from '../parts/tones';
import CorvinArt from '../../brand/CorvinArt';
import CorvinThinking from '../../corvin/CorvinThinking';

// Extracted verbatim from JobManagement's jobs tab block (Task 7), restyled
// to B2 primitives (Task 4 of the mechanic UI restyle). Presentational only
// — the loaders and mutations come from useJobs via the orchestrator; this
// tab calls no API itself. The action callbacks (showJobDetailsModal,
// runDiagnosis, openInvoiceModal, ...) stay in the orchestrator because they
// open modals it owns.
//
// The actions-dropdown panel (Update Status / Estimate / Invoice / View
// Diagnosis / Delete Job) is left exactly as it was — same classes, same
// console.logs — just relocated alongside the new Actions button. Only the
// row/card markup around it moved to DataTable + Badge + Button.
//
// Mechanic-agent drafting (Task 6): this is the one exception to "calls no
// API itself" above. Draft diagnosis / Draft estimate are proposals scoped
// to a single job and reviewed entirely inside a self-contained modal, so
// they call `jobAPI.draftDiagnosis` / `draftEstimate` directly rather than
// round-tripping through the orchestrator for a result nothing else needs.
// Nothing is written to the job automatically — "Use this" only pre-fills
// the mechanic's own existing estimate form (`openInvoiceModal` +
// `setEditableInvoice`/`setEditableParts`, both threaded down from
// `useInvoiceBuilder` in the orchestrator) for the mechanic to review and
// submit themselves; "Discard" drops the draft with no side effects at all.
const CONFIDENCE_STATUS = { high: 'success', medium: 'warning', low: 'danger' };
const SEVERITY_STATUS = { low: 'neutral', medium: 'warning', high: 'warning', critical: 'danger' };
const LINE_TYPE_LABEL = { part: 'Part', labor: 'Labor', fee: 'Fee' };

// Confidence + basis are required on every draft, and styled to look
// different at each level so a low-confidence draft cannot be mistaken for
// a confident one — same contract as the owner-facing AI page
// (components/ai/VehicleAIPage.js) since these figures can reach a
// customer invoice.
function ConfidenceBasis({ confidence, basis }) {
  return (
    <div className="flex flex-wrap items-center gap-2 pb-3 mb-3 border-b border-border-default">
      <Badge status={CONFIDENCE_STATUS[confidence] || 'neutral'}>{confidence} confidence</Badge>
      <span className="text-small text-ink-muted">{basis}</span>
    </div>
  );
}

// Standing, always-visible note on every estimate draft — not just low
// confidence ones. CROW holds no parts/labour pricing data (PartsTech
// removed, WorldPac disabled, labour rate hardcoded), so these figures are
// general-knowledge estimates, never a quote.
function EstimateStandingNote() {
  return (
    <div className="rounded-card border border-border-default bg-surface-sunken p-3 mb-4 flex items-start gap-2">
      <AlertTriangle size={18} strokeWidth={1.5} className="text-ink-muted mt-0.5 flex-shrink-0" />
      <p className="text-small text-ink-soft">
        Figures are estimated from general repair knowledge, <strong>not a parts catalogue</strong> —
        CROW doesn&apos;t hold live parts or labour pricing data. Review every line before using it.
      </p>
    </div>
  );
}

// Human wording for each status the drafting endpoints return. Never a raw
// error object, stack trace, or bare status code — a 503 is the state a
// fresh environment shows by default (feature flag off), so it gets the
// calmest treatment, not the most alarming.
function DraftError({ error }) {
  const status = error?.response?.status;

  if (status === 503) {
    return (
      <div className="rounded-card border border-border-default bg-surface-sunken p-4 flex items-start gap-3">
        <ShieldCheck size={20} strokeWidth={1.5} className="text-ink-faint mt-0.5 flex-shrink-0" />
        <p className="text-body text-ink-soft">
          The assistant is unavailable right now. It may be disabled in this environment, at its
          daily limit, or briefly unreachable — nothing on your end is broken. Try again shortly.
        </p>
      </div>
    );
  }

  if (status === 429) {
    return (
      <div className="rounded-card border border-warning-border bg-warning-tint p-4 flex items-start gap-3">
        <AlertTriangle size={20} strokeWidth={1.5} className="text-warning mt-0.5 flex-shrink-0" />
        <p className="text-body text-ink-soft">
          You&apos;ve used this hour&apos;s allowance for AI drafts. Try again in a bit.
        </p>
      </div>
    );
  }

  if (status === 400) {
    return (
      <div className="rounded-card border border-warning-border bg-warning-tint p-4 flex items-start gap-3">
        <AlertTriangle size={20} strokeWidth={1.5} className="text-warning mt-0.5 flex-shrink-0" />
        <p className="text-body text-ink-soft">
          That description is too long. Shorten it and try again.
        </p>
      </div>
    );
  }

  if (status === 404) {
    return (
      <div className="rounded-card border border-danger-border bg-danger-tint p-4 flex items-start gap-3">
        <AlertTriangle size={20} strokeWidth={1.5} className="text-danger mt-0.5 flex-shrink-0" />
        <p className="text-body text-ink-soft">
          Job not found. It may have been removed, or it isn&apos;t one of yours.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-card border border-danger-border bg-danger-tint p-4 flex items-start gap-3">
      <AlertTriangle size={20} strokeWidth={1.5} className="text-danger mt-0.5 flex-shrink-0" />
      <p className="text-body text-ink-soft">
        Something went wrong drafting this. Please try again.
      </p>
    </div>
  );
}

function DiagnosisDraftPanel({ result, onUse, onDiscard }) {
  return (
    <div className="rounded-card border border-border-default p-4">
      <ConfidenceBasis confidence={result.confidence} basis={result.basis} />

      <h4 className="text-body-strong text-ink mb-2">Possible causes</h4>
      <ul className="mb-4">
        {result.possible_causes.map((c, i) => (
          <li key={i} className="py-2 border-b border-border-default last:border-b-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-body-strong text-ink">{c.cause}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-small text-ink-muted">{c.likelihood} likelihood</span>
                <Badge status={SEVERITY_STATUS[c.severity] || 'neutral'}>{c.severity}</Badge>
              </div>
            </div>
            <p className="text-small text-ink-muted mt-1">{c.explanation}</p>
          </li>
        ))}
      </ul>

      {result.recommended_checks?.length > 0 && (
        <>
          <h4 className="text-body-strong text-ink mb-2">Recommended checks</h4>
          <ul className="list-disc list-inside space-y-1 mb-4">
            {result.recommended_checks.map((check, i) => (
              <li key={i} className="text-small text-ink-soft">{check}</li>
            ))}
          </ul>
        </>
      )}

      {result.safety_notes && (
        <div className="rounded-card border border-warning-border bg-warning-tint p-3 mb-4 flex items-start gap-2">
          <AlertTriangle size={18} strokeWidth={1.5} className="text-warning mt-0.5 flex-shrink-0" />
          <p className="text-small text-ink-soft">{result.safety_notes}</p>
        </div>
      )}

      <div className="flex items-center gap-2 pt-3 border-t border-border-default">
        <Button size="sm" onClick={onUse}>Use this</Button>
        <Button size="sm" variant="ghost" onClick={onDiscard}>Discard</Button>
      </div>
    </div>
  );
}

function EstimateDraftPanel({
  result, edits, onLineChange, onLaborHoursChange, onLaborRateChange, onUse, onDiscard
}) {
  return (
    <div className="rounded-card border border-border-default p-4">
      <ConfidenceBasis confidence={result.confidence} basis={result.basis} />
      <EstimateStandingNote />

      <h4 className="text-body-strong text-ink mb-2">Line items</h4>
      <div className="overflow-x-auto mb-4">
        <table className="w-full text-small">
          <thead>
            <tr className="text-label uppercase text-ink-muted text-left border-b border-border-default">
              <th className="py-1 pr-2">Description</th>
              <th className="py-1 pr-2">Type</th>
              <th className="py-1 pr-2 w-20">Qty</th>
              <th className="py-1 pr-2 w-28">Unit price</th>
              <th className="py-1 text-right w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            {edits.lines.map((line) => (
              <tr key={line.id} className="border-b border-border-default last:border-b-0">
                <td className="py-2 pr-2 align-top">
                  <p className="text-ink">{line.description}</p>
                  {(line.part_number || line.oem_part) && (
                    <p className="text-small text-ink-muted">
                      {line.part_number}
                      {line.part_number && line.oem_part ? ' · ' : ''}
                      {line.oem_part ? 'OEM' : ''}
                    </p>
                  )}
                </td>
                <td className="py-2 pr-2 align-top text-ink-muted">
                  {LINE_TYPE_LABEL[line.type] || line.type}
                </td>
                <td className="py-2 pr-2 align-top">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={line.quantity}
                    onChange={(e) => onLineChange(line.id, 'quantity', e.target.value)}
                    className="w-16 min-h-touch px-2 rounded-control border border-border-strong bg-surface-card text-ink focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15"
                  />
                </td>
                <td className="py-2 pr-2 align-top">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.unit_price}
                    onChange={(e) => onLineChange(line.id, 'unit_price', e.target.value)}
                    className="w-24 min-h-touch px-2 rounded-control border border-border-strong bg-surface-card text-ink focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15"
                  />
                </td>
                <td className="py-2 align-top text-right font-mono tabular-nums text-ink">
                  ${lineTotal(line.quantity, line.unit_price).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {result.labor_description && (
        <p className="text-small text-ink-soft mb-3">{result.labor_description}</p>
      )}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <FormField label="Labor hours">
          <Input
            type="number"
            min="0"
            step="0.25"
            value={edits.laborHours}
            onChange={(e) => onLaborHoursChange(e.target.value)}
          />
        </FormField>
        <FormField label="Suggested labor rate">
          <Input
            type="number"
            min="0"
            step="1"
            value={edits.laborRate}
            onChange={(e) => onLaborRateChange(e.target.value)}
          />
        </FormField>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-border-default">
        <Button size="sm" onClick={onUse}>Use this</Button>
        <Button size="sm" variant="ghost" onClick={onDiscard}>Discard</Button>
      </div>
    </div>
  );
}

const JobsTab = ({
  isLoading,
  jobs,
  jobSearch,
  setJobSearch,
  jobsPage,
  setJobsPage,
  jobsTotalPages,
  loadJobs,
  deleteJob,
  showJobDetailsModal,
  viewExistingDiagnosis,
  viewJobDiagnosis,
  runDiagnosis,
  showStatusUpdateModal,
  openInvoiceModal,
  setEditableInvoice,
  setEditableParts,
  selectedJobForActions,
  setSelectedJobForActions
}) => {
  // DataTable wraps its table in overflow-x-auto (DataTable.js:211-216), which
  // per CSS also makes overflow-y compute to auto — a scroll container on both
  // axes. An absolutely-positioned menu inside a <td> in there gets clipped on
  // lower rows and z-index cannot rescue it. So the open menu is portalled to
  // document.body and positioned from the trigger's getBoundingClientRect().
  //
  // JobManagement's click-outside handler keys off `.closest('.actions-dropdown')`
  // on mousedown; once portalled, the menu is no longer a DOM descendant of its
  // trigger wrapper, so the portalled element must carry the actions-dropdown
  // class itself or the handler closes the menu before any item's onClick fires.
  const [menuPosition, setMenuPosition] = useState(null);

  // Mechanic-agent drafting flow state. `null` when no draft modal is open.
  // { job, kind: 'diagnosis' | 'estimate', symptom, status: 'idle'|'loading'|'error'|'ready', error, result }
  const [draftFlow, setDraftFlow] = useState(null);
  // Editable working copy of an estimate draft's line items / labor fields,
  // seeded from the server response once a draft is ready. Kept separate
  // from `draftFlow.result` so the original AI draft is never mutated —
  // only what the mechanic explicitly edits before "Use this" is applied.
  const [estimateEdits, setEstimateEdits] = useState(null);

  // Estimated rendered height of the menu: 4 items x ~36px each (px-4 py-2
  // text-sm => 8px+8px vertical padding + 20px text-sm line-height, icons at
  // 16px fit inside that) + the wrapper's py-1 (4px top + 4px bottom). A
  // constant (measured against the actual four-item markup below) is used
  // instead of measuring the rendered node, since the menu doesn't exist in
  // the DOM yet at the moment we need to decide which way to open it.
  const MENU_HEIGHT_ESTIMATE_PX = 160;
  const MENU_GAP_PX = 8; // matches the original absolute menu's `mt-2`

  const handleToggleActions = (e, job) => {
    const isOpening = selectedJobForActions?.id !== job.id;
    if (isOpening) {
      const rect = e.currentTarget.getBoundingClientRect();
      const right = window.innerWidth - rect.right;
      const spaceBelow = window.innerHeight - rect.bottom;
      // Flip upward when there isn't room below in the actual browser
      // viewport (independent of DataTable's own overflow-x-auto scroll
      // container) — otherwise the menu renders partly/wholly below the
      // fold, and since the menu closes on scroll (see effect below), the
      // user's instinct to scroll toward it would just close it instead.
      if (spaceBelow < MENU_HEIGHT_ESTIMATE_PX) {
        setMenuPosition({ bottom: window.innerHeight - rect.top + MENU_GAP_PX, right });
      } else {
        setMenuPosition({ top: rect.bottom + MENU_GAP_PX, right });
      }
    } else {
      setMenuPosition(null);
    }
    setSelectedJobForActions(isOpening ? job : null);
  };

  // A portalled menu positioned from a one-time getBoundingClientRect() does
  // not follow its trigger if the page scrolls while it's open. Closing on
  // scroll (rather than repositioning on every scroll event) keeps this simple
  // and matches the existing click-outside-to-close behavior.
  //
  // This listener MUST clear only menuPosition — never selectedJobForActions.
  // A capture-phase `scroll` listener on window receives non-bubbling scroll
  // events from ANY descendant, including nodes portalled into <body>. The
  // shared Modal portals into <body> with a `fixed inset-0 overflow-y-auto`
  // scroll container, and both the Invoice and Status modals are mounted
  // behind `showInvoiceModal && selectedJobForActions &&` in the orchestrator.
  // Clearing selectedJobForActions here therefore unmounted the invoice
  // builder the moment the user scrolled down inside it to reach Save, and
  // reopening re-ran openInvoiceModal, which overwrites editableInvoice /
  // editableParts — the edits were unrecoverable. The menu render below is
  // already gated on menuPosition, so clearing that alone still hides the
  // dropdown without touching orchestrator state.
  useEffect(() => {
    if (!menuPosition) return undefined;
    const handleScroll = () => setMenuPosition(null);
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handleScroll, { capture: true });
  }, [menuPosition]);

  // ---------------------------------------------------------------------
  // Mechanic-agent drafting (Task 6)
  // ---------------------------------------------------------------------
  const openDraftFlow = (job, kind) => {
    // Close the actions dropdown this was launched from.
    setMenuPosition(null);
    setSelectedJobForActions(null);
    setEstimateEdits(null);
    setDraftFlow({
      job,
      kind,
      // Full text FIRST: symptom_summary is LEFT(symptom_description, 100),
      // a display string for the table cell. Seeding the draft from it feeds
      // the model a complaint cut off mid-word.
      symptom: job.symptom_description || job.symptom_summary || '',
      status: 'idle',
      error: null,
      result: null,
    });
  };

  const closeDraftFlow = () => {
    setDraftFlow(null);
    setEstimateEdits(null);
  };

  const handleDraftSymptomChange = (value) => {
    setDraftFlow((prev) => (prev ? { ...prev, symptom: value } : prev));
  };

  const handleRunDraft = async () => {
    if (!draftFlow || !draftFlow.symptom.trim() || draftFlow.status === 'loading') return;
    const { job, kind, symptom } = draftFlow;
    setDraftFlow((prev) => (prev ? { ...prev, status: 'loading', error: null } : prev));
    try {
      const result = kind === 'diagnosis'
        ? await jobAPI.draftDiagnosis(job.id, symptom.trim())
        : await jobAPI.draftEstimate(job.id, symptom.trim());

      setDraftFlow((prev) => (prev ? { ...prev, status: 'ready', result, error: null } : prev));

      if (kind === 'estimate') {
        setEstimateEdits({
          lines: (result.line_items || []).map((li, i) => ({
            id: i,
            type: li.type,
            description: li.description,
            part_number: li.part_number || '',
            oem_part: !!li.oem_part,
            quantity: li.quantity,
            unit_price: li.unit_price,
          })),
          laborHours: result.labor_hours ?? 0,
          laborRate: result.suggested_labor_rate ?? '',
        });
      }
    } catch (error) {
      setDraftFlow((prev) => (prev ? { ...prev, status: 'error', error, result: null } : prev));
    }
  };

  const updateEstimateLine = (id, field, value) => {
    setEstimateEdits((prev) => prev && ({
      ...prev,
      lines: prev.lines.map((line) => (line.id === id ? { ...line, [field]: value } : line)),
    }));
  };

  // "Use this" for a diagnosis draft: there is no separate diagnosis form to
  // populate, so it feeds into the same estimate form the mechanic already
  // submits — opening it and appending a readable summary of the causes and
  // safety notes to its notes field. Nothing is written to the job itself.
  const handleUseDiagnosis = async () => {
    if (!draftFlow?.result) return;
    const { job, result } = draftFlow;
    const causesText = (result.possible_causes || [])
      .map((c) => `- ${c.cause} (${c.likelihood} likelihood, ${c.severity} severity): ${c.explanation}`)
      .join('\n');
    const checksText = result.recommended_checks?.length
      ? `\nRecommended checks:\n${result.recommended_checks.map((c) => `- ${c}`).join('\n')}`
      : '';
    const safetyText = result.safety_notes ? `\nSafety notes: ${result.safety_notes}` : '';
    const summary = `AI-drafted diagnosis (${result.confidence} confidence — ${result.basis}):\n${causesText}${checksText}${safetyText}`;

    await openInvoiceModal(job);
    setEditableInvoice((prev) => ({
      ...prev,
      notes: [prev.notes, summary].filter(Boolean).join('\n\n'),
    }));
    closeDraftFlow();
  };

  // "Use this" for an estimate draft: populates the existing estimate form
  // (editableParts / editableInvoice, both owned by useInvoiceBuilder in the
  // orchestrator) with the mechanic's edited copy of the draft, then opens
  // that same form for final review — it is not saved or sent by this
  // action, only pre-filled.
  const handleUseEstimate = async () => {
    if (!draftFlow?.result || !estimateEdits) return;
    const { job, result } = draftFlow;
    const parts = estimateEdits.lines
      .filter((line) => line.type !== 'labor')
      .map((line) => ({
        id: Date.now() + line.id,
        part_name: line.description,
        part_number: line.part_number,
        quantity: parseFloat(line.quantity) || 1,
        unit_cost: parseFloat(line.unit_price) || 0,
      }));
    const note = `AI-drafted estimate (${result.confidence} confidence — ${result.basis}).${result.labor_description ? ` ${result.labor_description}` : ''}`;

    await openInvoiceModal(job);
    setEditableParts(parts);
    setEditableInvoice((prev) => ({
      ...prev,
      labor_hours: parseFloat(estimateEdits.laborHours) || 0,
      labor_rate: parseFloat(estimateEdits.laborRate) || prev.labor_rate,
      notes: [prev.notes, note].filter(Boolean).join('\n\n'),
    }));
    closeDraftFlow();
  };

  // The menu is rendered ONCE, here at the component level, rather than from
  // the column cell and the card renderer. DataTable keeps both the table
  // (`hidden sm:block`) and the cards (`sm:hidden`) in the DOM at all times
  // and only hides one with CSS; since a portal escapes its React-tree
  // ancestors, a `display: none` ancestor no longer hides it. Calling the
  // renderer from both places therefore put two identical, exactly-stacked
  // copies of all four items under <body> — assistive tech announced eight
  // items and role-based queries broke on "multiple elements". The cell and
  // the card now render only the trigger button.
  //
  // The portalled root must keep the `actions-dropdown` class: JobManagement's
  // click-outside handler keys off `event.target.closest('.actions-dropdown')`,
  // and once portalled the menu is no longer a DOM descendant of its trigger
  // wrapper, so without the class the handler would close the menu before any
  // item's onClick could fire.
  const actionsMenu = selectedJobForActions && menuPosition && createPortal(
    <div
      className="actions-dropdown fixed w-48 bg-surface-card rounded-md shadow-popover z-50 border border-border-default"
      style={menuPosition}
    >
      <div className="py-1">
        <button
          onClick={() => showStatusUpdateModal(selectedJobForActions)}
          className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-surface-sunken"
        >
          <Clock className="h-4 w-4 inline mr-2" />
          Update Status
        </button>
        <button
          onClick={() => openInvoiceModal(selectedJobForActions)}
          className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-surface-sunken"
        >
          <DollarSign className="h-4 w-4 inline mr-2" />
          Estimate / Invoice
        </button>
        <button
          onClick={() => viewJobDiagnosis(selectedJobForActions)}
          className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-surface-sunken"
        >
          <Brain className="h-4 w-4 inline mr-2" />
          View Diagnosis
        </button>
        <div className="my-1 border-t border-border-default" />
        <button
          onClick={() => openDraftFlow(selectedJobForActions, 'diagnosis')}
          className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-surface-sunken"
        >
          <Stethoscope className="h-4 w-4 inline mr-2" />
          Draft diagnosis (AI)
        </button>
        <button
          onClick={() => openDraftFlow(selectedJobForActions, 'estimate')}
          className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-surface-sunken"
        >
          <Receipt className="h-4 w-4 inline mr-2" />
          Draft estimate (AI)
        </button>
        <div className="my-1 border-t border-border-default" />
        <button
          onClick={() => deleteJob(selectedJobForActions)}
          className="block w-full text-left px-4 py-2 text-sm text-danger hover:bg-danger-tint"
        >
          <X className="h-4 w-4 inline mr-2" />
          Delete Job
        </button>
      </div>
    </div>,
    document.body
  );

  const columns = [
    {
      header: 'Job',
      accessor: 'job_number',
      render: (j) => <span className="font-mono tabular-nums text-ink font-medium">{j.job_number}</span>,
    },
    {
      header: 'Customer',
      accessor: 'customer_name',
      render: (j) => <span>{j.customer_name || '—'}</span>,
    },
    {
      header: 'Vehicle',
      accessor: 'vehicle_display',
      render: (j) => <span className="text-ink-soft">{j.vehicle_display || '—'}</span>,
    },
    {
      header: 'Status',
      accessor: 'job_status',
      render: (j) => (
        <Badge status={STATUS_TONE[j.job_status] || 'neutral'}>
          {String(j.job_status || '').replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      header: 'Priority',
      accessor: 'priority',
      render: (j) => (
        <Badge status={PRIORITY_TONE[j.priority] || 'neutral'}>{j.priority}</Badge>
      ),
    },
    {
      header: 'Created',
      accessor: 'created_at',
      render: (j) => (
        <span className="font-mono tabular-nums text-ink-soft">
          {j.created_at ? new Date(j.created_at).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      header: '',
      accessor: 'actions',
      sortable: false,
      // Pinned: the table is ~1240px wide, so on laptops and tablets this
      // column (and its Actions menu, the way into estimates) was cut off.
      stickyRight: true,
      render: (j) => (
        <div className="flex items-center justify-end gap-1">
          {(j.job_status === 'created' || j.job_status === 'diagnosing') && (
            <Button variant="ghost" size="sm" disabled={isLoading} onClick={() => runDiagnosis(j)}>
              <Brain size={16} strokeWidth={1.5} className="mr-1" />
              {j.job_status === 'diagnosing' ? 'Re-Diagnose' : 'Diagnose'}
            </Button>
          )}
          {j.job_status === 'diagnosed' && (
            <Button variant="ghost" size="sm" onClick={() => viewExistingDiagnosis(j)}>
              <Eye size={16} strokeWidth={1.5} className="mr-1" />Results
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => showJobDetailsModal(j)}>
            <FileText size={16} strokeWidth={1.5} className="mr-1" />Details
          </Button>
          <div className="relative actions-dropdown">
            <Button
              variant="secondary"
              size="sm"
              className="actions-dropdown"
              onClick={(e) => handleToggleActions(e, j)}
            >
              <Settings size={16} strokeWidth={1.5} className="mr-1" />Actions
            </Button>
          </div>
        </div>
      ),
    },
  ];

  const renderJobCard = (j) => (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono tabular-nums text-ink font-medium">{j.job_number}</span>
        <Badge status={STATUS_TONE[j.job_status] || 'neutral'}>
          {String(j.job_status || '').replace(/_/g, ' ')}
        </Badge>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-small text-ink-muted">
        <span className="inline-flex items-center gap-1">
          <User size={14} strokeWidth={1.5} />{j.customer_name || '—'}
        </span>
        <span className="inline-flex items-center gap-1">
          <Car size={14} strokeWidth={1.5} />{j.vehicle_display || '—'}
        </span>
        <span className="inline-flex items-center gap-1 font-mono tabular-nums">
          <Calendar size={14} strokeWidth={1.5} />
          {j.created_at ? new Date(j.created_at).toLocaleDateString() : '—'}
        </span>
      </div>
      {(j.symptom_summary || j.symptom_description) && (
        <p className="mt-2 text-body text-ink-soft">{j.symptom_summary || j.symptom_description}</p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {(j.job_status === 'created' || j.job_status === 'diagnosing') && (
          <Button variant="ghost" size="sm" className="min-h-touch" disabled={isLoading} onClick={() => runDiagnosis(j)}>
            <Brain size={16} strokeWidth={1.5} className="mr-1" />
            {j.job_status === 'diagnosing' ? 'Re-Diagnose' : 'Diagnose'}
          </Button>
        )}
        {j.job_status === 'diagnosed' && (
          <Button variant="ghost" size="sm" className="min-h-touch" onClick={() => viewExistingDiagnosis(j)}>
            <Eye size={16} strokeWidth={1.5} className="mr-1" />Results
          </Button>
        )}
        <Button variant="ghost" size="sm" className="min-h-touch" onClick={() => showJobDetailsModal(j)}>
          <FileText size={16} strokeWidth={1.5} className="mr-1" />Details
        </Button>
        <div className="relative actions-dropdown">
          <Button
            variant="secondary"
            size="sm"
            className="actions-dropdown min-h-touch"
            onClick={(e) => handleToggleActions(e, j)}
          >
            <Settings size={16} strokeWidth={1.5} className="mr-1" />Actions
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-surface-card rounded-card shadow-elevation-1 p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center sm:justify-between">
                <div className="flex items-center flex-1 sm:mr-4 border border-border-strong rounded-control bg-surface-card px-3 min-h-touch focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/15">
                  <Search className="h-5 w-5 text-ink-muted mr-2 sm:mr-3 flex-shrink-0" />
                  <input
                    type="text"
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    placeholder="Search jobs..."
                    aria-label="Search jobs"
                    className="flex-1 border-0 focus:ring-0 focus:outline-none text-ink placeholder:text-ink-muted text-body min-w-0"
                  />
                  {jobSearch && (
                    <button
                      onClick={() => setJobSearch('')}
                      aria-label="Clear search"
                      className="min-w-touch min-h-touch -mr-3 flex items-center justify-center text-ink-muted hover:text-ink flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {/* Secondary, and labelled at every width: "New Job" is the
                    screen's one solid accent action, and the icon-only mobile
                    variant had no accessible name. */}
                <Button variant="secondary" icon={RefreshCw} loading={isLoading} onClick={() => loadJobs()}>
                  Refresh
                </Button>
              </div>
            </div>

            {/* Jobs List */}
            <div className="bg-surface-card rounded-card shadow-elevation-1 overflow-hidden">
              <div className="px-6 py-4 border-b border-border-default">
                <h3 className="text-lg font-medium text-ink">Recent Jobs</h3>
              </div>
              {jobs.length === 0 && !isLoading ? (
                <EmptyState
                  illustration={<CorvinArt pose="standing" size={96} />}
                  title="No jobs yet"
                  description="Create a job when the first car comes in. A customer and a complaint are enough to start."
                />
              ) : (
                <DataTable
                  data={jobs}
                  columns={columns}
                  density="compact"
                  loading={isLoading}
                  pagination={false}
                  filterable={false}
                  sortable={false}
                  renderCard={renderJobCard}
                />
              )}

              {/* Pagination */}
              {jobsTotalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-border-default">
                  <button
                    onClick={() => setJobsPage(p => Math.max(1, p - 1))}
                    disabled={jobsPage === 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-surface-sunken"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-ink-muted">
                    Page {jobsPage} of {jobsTotalPages}
                  </span>
                  <button
                    onClick={() => setJobsPage(p => Math.min(jobsTotalPages, p + 1))}
                    disabled={jobsPage === jobsTotalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-surface-sunken"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            {/* The single portalled Actions menu — see the note at its
                definition for why it is not rendered from the row/card. */}
            {actionsMenu}

            {/* Mechanic-agent drafting modal (Task 6) — self-contained: owns
                its own request state and is the only place in this component
                that calls an API directly. See the note above the component
                for why. */}
            {draftFlow && (
              <Modal
                isOpen
                onClose={closeDraftFlow}
                title={`${draftFlow.kind === 'diagnosis' ? 'Draft diagnosis' : 'Draft estimate'} — ${draftFlow.job.job_number}`}
                size="lg"
              >
                <div className="space-y-4">
                  <FormField
                    label="Symptom"
                    hint="The assistant drafts from this description — edit it before drafting or redrafting."
                  >
                    <Textarea
                      rows={3}
                      value={draftFlow.symptom}
                      onChange={(e) => handleDraftSymptomChange(e.target.value)}
                      disabled={draftFlow.status === 'loading'}
                    />
                  </FormField>

                  <Button
                    onClick={handleRunDraft}
                    loading={draftFlow.status === 'loading'}
                    disabled={!draftFlow.symptom.trim() || draftFlow.status === 'loading'}
                  >
                    {draftFlow.status === 'ready'
                      ? 'Redraft'
                      : (draftFlow.kind === 'diagnosis' ? 'Draft diagnosis' : 'Draft estimate')}
                  </Button>

                  {draftFlow.status === 'loading' && (
                    <CorvinThinking label="Corvin is drafting this. It can take a minute or two." />
                  )}

                  {draftFlow.status === 'error' && <DraftError error={draftFlow.error} />}

                  {draftFlow.status === 'ready' && draftFlow.kind === 'diagnosis' && (
                    <DiagnosisDraftPanel
                      result={draftFlow.result}
                      onUse={handleUseDiagnosis}
                      onDiscard={closeDraftFlow}
                    />
                  )}

                  {draftFlow.status === 'ready' && draftFlow.kind === 'estimate' && estimateEdits && (
                    <EstimateDraftPanel
                      result={draftFlow.result}
                      edits={estimateEdits}
                      onLineChange={updateEstimateLine}
                      onLaborHoursChange={(v) => setEstimateEdits((prev) => prev && ({ ...prev, laborHours: v }))}
                      onLaborRateChange={(v) => setEstimateEdits((prev) => prev && ({ ...prev, laborRate: v }))}
                      onUse={handleUseEstimate}
                      onDiscard={closeDraftFlow}
                    />
                  )}
                </div>
              </Modal>
            )}
          </div>
  );
};

export default JobsTab;
